﻿import {isNullOrEmpty} from "@2d-front-monorepo/core";
import React, {JSX, ReactNode, useMemo} from "react";
import MaterialIcon from "../MaterialIcon";
import {PrefixSuffixType} from "./HtmlControlProps";

export type LabeledControlAriaProps = {
	describedBy?: string;
	invalid?: boolean;
}

export type LabeledControlProps = {
	label: ReactNode,
	type: string,
	id: string,
	column?: string,
	hint?: string,
	error?: string,
	required?: boolean,
	hidden?: boolean,
	children?: ReactNode,
	ariaRef?: React.RefObject<LabeledControlAriaProps | undefined>;
	controlPrefix?: PrefixSuffixType;
	controlSuffix?: PrefixSuffixType;
	ref?: React.RefObject<HTMLSpanElement | null>;
};

export default function LabeledControl(props: LabeledControlProps): JSX.Element {
	const {label, type, id, column, hint, error, required, hidden, children, ariaRef, controlPrefix, controlSuffix, ref} = props;

	const hintId = id + "-hint";
	const errorId = id + "-error";
	const hasError = !isNullOrEmpty(error);
	const hasHint = hint != null;

	if (ariaRef) {
		ariaRef.current = {
			describedBy: ((hasHint ? hintId : "") + (hasError ? " " + errorId : "")).trimStart(),
			invalid: hasError
		};
	}

	const klass = useMemo(() => "labelled-field" + (column ? ` ${column}` : ""), [column]);
	const klassType = useMemo(() => "form__field form__field--" + type, [type]);

	if (hidden) {
		return <></>;
	}

	return (
		<div className={klass}>
			<div className={klassType}>
				<label className={"field__label" + (hasError ? " field__label--error" : "")} htmlFor={id}>
                    <span className="label__text">
                        {label}
	                    {required && <span className="field__star" aria-hidden="true">*</span>}
                    </span>
				</label>
				<span className={"field__input" + (hasError ? " field__input--error" : "")} ref={ref}>
					{controlPrefix && <span className="field__prefix">{controlPrefix}</span>}
					{children}
					{controlSuffix && <span className="field__suffix">{controlSuffix}</span>}
                </span>
				<span
					id={errorId}
					className="label__error"
					aria-live="polite"
					aria-hidden={!error}
					style={{visibility: error ? "visible" : "hidden"}}>
					<MaterialIcon icon="error"/>
                    <span>{error}</span>
                </span>
				{hasHint ? <span id={hintId} className="field__hint">{hint}</span> : null}
			</div>
		</div>
	);
};
